import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, useEffect, Dispatch, SetStateAction } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  VaultExportModel,
  VaultKeys,
  getVaultExports,
  VaultExportFileModel,
} from '../../../api/vault-api';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import {
  VaultRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';

import VaultExportToolbar from './vault-export-toolbar';
import CreateVaultExportDialog from './create-vault-export-dialog';
import DeleteVaultExportsDialog from './delete-vault-exports-dialog';
import VaultExportDrawerDetails from './vault-export-drawer-details';
import VaultExportDrawerDetailsOld from './vault-export-drawer-details-old';
import {
  DisabledTooltipMenuItem,
  TooltipGridActionsCellItem,
} from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import {
  formatBytes,
  getDeletePermissionForCorpus,
} from '../../../utils/vault-utils';
import { Messages } from '../../../utils/message-constants';
import { isFeatureEnabled } from '../../../utils/feature-flags';

import Drawer from '@mui/material/Drawer';
import Chip from '@mui/material/Chip';
import Delete from '@mui/icons-material/Delete';
import AttachEmail from '@mui/icons-material/AttachEmail';

export interface ExtendedVaultExportModel extends VaultExportModel {
  totalArtifactCount?: number;
  dataSizeBytes?: number;
  matterName?: string;
}

interface VaultExportGridProps {
  toolsRoles: string[];
  matterId?: string;
  matterName?: string;
  openAddVaultExport?: boolean;
  setOpenAddVaultExport?: Dispatch<SetStateAction<boolean>>;
  allExports?: ExtendedVaultExportModel[];
  loading?: boolean;
}

export default function VaultExportGrid({
  toolsRoles,
  matterId = '',
  matterName,
  openAddVaultExport = false,
  setOpenAddVaultExport = () => {},
  allExports,
  loading: parentLoading,
}: VaultExportGridProps) {
  const { viewType, features } = useUserSettings();
  const uiEnhancementPhase2Enabled = isFeatureEnabled(
    'uiEnhancementPhase2',
    features
  );
  const [openDeleteVaultExports, setOpenDeleteVaultExports] = useState(false);
  const [selectedExports, setSelectedExports] = useState<string[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });
  const [pageTokens, setPageTokens] = useState<
    Record<number, string | undefined>
  >({
    0: undefined,
  });

  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: 100 });
    setPageTokens({ 0: undefined });
  }, [matterId]);

  const {
    data: vaultExports,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      VaultKeys.VaultExports,
      matterId,
      paginationModel.pageSize,
      pageTokens[paginationModel.page],
    ],
    queryFn: () =>
      getVaultExports(
        matterId,
        paginationModel.pageSize,
        pageTokens[paginationModel.page]
      ).then((page) => {
        if (!page || !page.results) return page;
        // compute derived UI fields on each export: ensure dataSizeBytes and totalArtifactCount are present
        const mapped = page.results.map((exp) => {
          // if backend already provided dataSizeBytes, use it; otherwise sum files if available on the object
          const files = exp.cloudStorageSink?.files || [];
          const computedSize = files.reduce(
            (s: number, f: VaultExportFileModel) => s + (f?.size ?? 0),
            0
          );
          return {
            ...exp,
            totalArtifactCount: exp.exportStats?.totalArtifactCount,
            dataSizeBytes: computedSize,
          } as ExtendedVaultExportModel;
        });
        return { ...page, results: mapped };
      }),
    enabled: !!matterId,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (vaultExports?.cursor) {
      setPageTokens((prev) => ({
        ...prev,
        [paginationModel.page + 1]: vaultExports.cursor as string | undefined,
      }));
    }
  }, [vaultExports?.cursor, paginationModel.page]);

  const rows = useMemo<ExtendedVaultExportModel[]>(() => {
    const rawRows = (allExports ||
      vaultExports?.results ||
      []) as ExtendedVaultExportModel[];
    const hasViewAll = isAbleToSeeUIElement(
      toolsRoles,
      VaultRoles.ViewVaultExports.value
    );
    if (hasViewAll) {
      return rawRows;
    }

    return rawRows.filter((exp) => {
      const corpus = exp.corpus;
      if (!corpus) return false;
      switch (corpus) {
        case 'MAIL':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewGmailVaultExport.value
          );
        case 'HANGOUTS_CHAT':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewChatVaultExport.value
          );
        case 'GEMINI':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewGeminiVaultExport.value
          );
        case 'CALENDAR':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewCalendarVaultExport.value
          );
        case 'DRIVE':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewDriveVaultExport.value
          );
        case 'GROUPS':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewGroupsVaultExport.value
          );
        default:
          return false;
      }
    });
  }, [allExports, vaultExports?.results, toolsRoles]);

  const selectedExportIdFromUrl = searchParams.get('selectedExport');
  const selectedVaultExportModel = useMemo(() => {
    return (
      rows.find((r) => r.id.toString() === selectedExportIdFromUrl) || null
    );
  }, [rows, selectedExportIdFromUrl]);

  const openDrawer =
    !!selectedVaultExportModel && !(allExports ? parentLoading : isLoading);

  const handleCloseDrawer = () => {
    setSearchParams((prev) => {
      prev.delete('selectedExport');
      return prev;
    });
  };

  const columns: GridColDef<ExtendedVaultExportModel>[] = useMemo(() => {
    const cols: GridColDef<ExtendedVaultExportModel>[] = [];

    cols.push({
      field: 'name',
      headerName: 'Export Name',
      flex: 1,
    });

    if (!matterId) {
      cols.push({
        field: 'matterName',
        headerName: 'Matter Name',
        flex: 1,
      });
    }

    cols.push(
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        renderCell: (params) => {
          const statusValue = params.row.status?.toUpperCase();
          if (!statusValue) return null;

          let statusColor = 'text.secondary';
          let label = statusValue;
          let icon = null;

          if (statusValue === 'COMPLETED') {
            statusColor = 'success.main';
            label = 'Complete';
            icon = <CheckIcon fontSize="small" sx={{ color: 'inherit' }} />;
          } else if (statusValue === 'FAILED') {
            statusColor = 'error.main';
            label = 'Failed';
            icon = <CloseIcon fontSize="small" sx={{ color: 'inherit' }} />;
          } else if (
            statusValue === 'IN_PROGRESS' ||
            statusValue === 'INPROGRESS' ||
            statusValue === 'RUNNING'
          ) {
            statusColor = 'primary.main';
            label = 'Running';
            icon = <PlayArrowIcon fontSize="small" sx={{ color: 'inherit' }} />;
          }

          return (
            <Chip
              icon={icon ?? undefined}
              label={label}
              size="small"
              sx={{
                color: statusColor,
                border: '1px solid',
                bgcolor: 'background.paper',
                '& .MuiChip-icon': {
                  color: 'inherit',
                },
              }}
            />
          );
        },
      },
      {
        field: 'createTime',
        headerName: 'Create Time',
        width: 200,
        type: 'date',
        valueGetter: (_params, row) => new Date(row.createTime),
        valueFormatter: (params: Date) =>
          dayjs(params).format('M/D/YY, h:mm A'),
      },
      {
        field: 'totalArtifactCount',
        headerName: 'Count',
        width: 150,
        valueGetter: (_params, row) => row.totalArtifactCount ?? undefined,
        valueFormatter: (params) =>
          params != null ? (params as number).toLocaleString() : '—',
      },
      {
        field: 'dataSizeBytes',
        headerName: 'Data Size',
        width: 150,
        valueGetter: (_params, row) => row.dataSizeBytes ?? undefined,
        valueFormatter: (params) =>
          params != null ? formatBytes(params as number) : '—',
      },
      {
        field: 'daysUntilDeletion',
        headerName: 'Days Until Deletion',
        width: 180,
        renderCell: (params) => {
          const createDate = dayjs(params.row.createTime);
          const expiryDate = createDate.add(15, 'day');
          const diffDays = expiryDate.diff(dayjs(), 'day');
          if (diffDays <= 0) {
            return 'Expired';
          }
          return `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
        },
      },

      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const hasGlobalDelete = isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.DeleteVaultExports.value
          );
          const requiredPermission = getDeletePermissionForCorpus(
            params.row.corpus
          );
          const hasSpecificDelete = isAbleToSeeUIElement(
            toolsRoles,
            requiredPermission.value
          );
          const canDelete = hasGlobalDelete || hasSpecificDelete;

          return [
            <TooltipGridActionsCellItem
              key="delete"
              label="Delete"
              icon={<Delete />}
              disabled={!canDelete}
              tooltipText={
                canDelete
                  ? ''
                  : Messages.Errors.Permission.Acting(requiredPermission.name)
              }
              onClick={() => {
                setSelectedExports([params.row.id]);
                setOpenDeleteVaultExports(true);
              }}
              showInMenu
            />,
          ];
        },
      }
    );

    return cols;
  }, [toolsRoles, matterId]);

  const toolbarProps = useMemo(
    () => ({
      matterId,
      setOpenAddVaultExport,
      setOpenDeleteVaultExports,
      selectedExports,
      rows,
    }),
    [
      matterId,
      setOpenAddVaultExport,
      setOpenDeleteVaultExports,
      selectedExports,
      rows,
    ]
  );

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <SharedDataGridClient
          gridStateKey={!matterId ? 'vaultExportsGridStates' : undefined}
          gridKey={
            !matterId
              ? viewType?.vaultExportsGridStates?.activeGridState
              : undefined
          }
          slots={{
            toolbar: useToolbar(VaultExportToolbar, toolbarProps),
            ...(allExports
              ? createGridOverlays('Vault Exports', <AttachEmail />)
              : !matterId
              ? createGridOverlays(
                  'No Matter has been selected',
                  <AttachEmail />,
                  '',
                  '',
                  true
                )
              : createGridOverlays('Vault Exports', <AttachEmail />)),
            baseMenuItem: DisabledTooltipMenuItem,
          }}
          columns={columns}
          rows={rows}
          loading={allExports ? parentLoading : isLoading || isFetching}
          getRowId={(row) => row.id}
          setSelectedRows={setSelectedExports}
          selectedRows={selectedExports}
          checkboxSelection
          pagination={!allExports}
          paginationMode={allExports ? 'client' : 'server'}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            if (model.pageSize !== paginationModel.pageSize) {
              setPageTokens({ 0: undefined });
              setPaginationModel({ page: 0, pageSize: model.pageSize });
            } else {
              setPaginationModel(model);
            }
          }}
          rowCount={
            allExports
              ? rows.length
              : vaultExports?.cursor
              ? -1
              : paginationModel.page * paginationModel.pageSize +
                (vaultExports?.results?.length || 0)
          }
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            ...(allExports && {
              '& .MuiDataGrid-footerContainer': { display: 'none' },
            }),
          }}
          onRowClick={(params, event) => {
            const target = event.target as HTMLElement;
            if (
              target.closest('.MuiDataGrid-actionsCell') ||
              target.closest('[role="menuitem"]')
            ) {
              return;
            }
            setSearchParams((prev) => {
              prev.set('selectedExport', params.row.id.toString());
              return prev;
            });
          }}
          disableRowSelectionOnClick
        />
      </div>

      {openAddVaultExport && matterId && (
        <CreateVaultExportDialog
          open={openAddVaultExport}
          handleClose={() => setOpenAddVaultExport(false)}
          matterId={matterId}
          matterName={matterName}
        />
      )}

      {openDeleteVaultExports && selectedExports.length > 0 && (
        <DeleteVaultExportsDialog
          open={openDeleteVaultExports}
          handleClose={() => setOpenDeleteVaultExports(false)}
          selectedExportIds={selectedExports}
          allExports={rows}
          matterId={matterId}
          setSelectedExports={setSelectedExports}
        />
      )}

      {selectedVaultExportModel &&
        (uiEnhancementPhase2Enabled ? (
          <VaultExportDrawerDetails
            open={openDrawer}
            vaultExport={selectedVaultExportModel}
            onClose={handleCloseDrawer}
            matterName={matterName || selectedVaultExportModel.matterName}
          />
        ) : (
          <Drawer
            anchor="right"
            open={openDrawer}
            onClose={handleCloseDrawer}
            sx={{
              '& .MuiDrawer-paper': {
                width: { xs: '100vw', md: '45%' },
              },
            }}
          >
            <VaultExportDrawerDetailsOld
              vaultExport={selectedVaultExportModel}
              onClose={handleCloseDrawer}
              matterName={matterName || selectedVaultExportModel.matterName}
            />
          </Drawer>
        ))}
    </>
  );
}
